import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/firebaseConfig';

// Check-in utility functions

/**
 * Update check-in status for a specific group
 * @param {string} tripId - Trip plan ID
 * @param {string} groupId - Group ID (organizers, participants, girls)
 * @param {string} userId - User ID to check in
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export const updateCheckinStatus = async (tripId, groupId, userId) => {
  try {
    if (!tripId || !groupId || !userId) {
      throw new Error('Missing required parameters');
    }

    const tripRef = doc(db, 'tripPlans', tripId);
    const tripSnap = await getDoc(tripRef);
    
    if (!tripSnap.exists()) {
      throw new Error('Trip plan not found');
    }

    const tripData = tripSnap.data();
    const currentStatus = tripData.checkinConfig?.status || {
      organizersCheckedIn: [],
      participantsCheckedIn: [],
      girlsCheckedIn: []
    };

    const groupKey = `${groupId}CheckedIn`;
    const currentList = currentStatus[groupKey] || [];
    
    // Check if user is already checked in
    if (currentList.includes(userId)) {
      return {
        success: false,
        error: 'User already checked in'
      };
    }

    // Add user to checked in list
    const updatedList = [...currentList, userId];
    const updatedStatus = {
      ...currentStatus,
      [groupKey]: updatedList,
      lastCheckinTime: new Date().toISOString(),
      totalCheckins: (currentStatus.totalCheckins || 0) + 1
    };

    // Update trip plan
    await updateDoc(tripRef, {
      [`checkinConfig.status`]: updatedStatus,
      lastUpdated: new Date().toISOString()
    });

    // Update checklist if needed
    await updateCheckinChecklist(tripId, updatedStatus);

    return {
      success: true,
      data: updatedStatus
    };
  } catch (error) {
    console.error('Error updating check-in status:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Update checklist based on check-in status
 * @param {string} tripId - Trip plan ID
 * @param {Object} checkinStatus - Current check-in status
 */
export const updateCheckinChecklist = async (tripId, checkinStatus) => {
  try {
    const tripRef = doc(db, 'tripPlans', tripId);
    const tripSnap = await getDoc(tripRef);
    
    if (!tripSnap.exists()) {
      throw new Error('Trip plan not found');
    }

    const tripData = tripSnap.data();
    const currentChecklist = tripData.checklist || {};
    
    // Check if all required groups have checked in
    const checkinConfig = tripData.checkinConfig;
    if (!checkinConfig?.enabled) {
      return;
    }

    let allCheckedIn = true;
    let hasAnyCheckin = false;

    // Check organizers
    if (checkinConfig.enabled.organizers) {
      const organizersCheckedIn = checkinStatus.organizersCheckedIn || [];
      const organizersCount = getOrganizersCount(tripData);
      if (organizersCount > 0) {
        hasAnyCheckin = true;
        if (organizersCheckedIn.length < organizersCount) {
          allCheckedIn = false;
        }
      }
    }

    // Check participants
    if (checkinConfig.enabled.participants) {
      const participantsCheckedIn = checkinStatus.participantsCheckedIn || [];
      const participantsCount = getParticipantsCount(tripData);
      if (participantsCount > 0) {
        hasAnyCheckin = true;
        if (participantsCheckedIn.length < participantsCount) {
          allCheckedIn = false;
        }
      }
    }

    // Check girls
    if (checkinConfig.enabled.girls) {
      const girlsCheckedIn = checkinStatus.girlsCheckedIn || [];
      const girlsCount = getGirlsCount(tripData);
      if (girlsCount > 0) {
        hasAnyCheckin = true;
        if (girlsCheckedIn.length < girlsCount) {
          allCheckedIn = false;
        }
      }
    }

    // Update checklist
    const updatedChecklist = {
      ...currentChecklist,
      activityTrip: {
        ...currentChecklist.activityTrip,
        girlsCheckedIn: hasAnyCheckin ? allCheckedIn : false
      }
    };

    await updateDoc(tripRef, {
      checklist: updatedChecklist,
      lastUpdated: new Date().toISOString()
    });

  } catch (error) {
    console.error('Error updating check-in checklist:', error);
  }
};

/**
 * Get count of organizers (owner + helpers)
 * @param {Object} tripData - Trip plan data
 * @returns {number}
 */
export const getOrganizersCount = (tripData) => {
  let count = 1; // Owner
  if (tripData.helpers && Array.isArray(tripData.helpers)) {
    count += tripData.helpers.length;
  }
  return count;
};

/**
 * Get count of participants
 * @param {Object} tripData - Trip plan data
 * @returns {number}
 */
export const getParticipantsCount = (tripData) => {
  // This would need to be implemented based on booking data
  // For now, return 0 as participants are not stored in trip plan
  return 0;
};

/**
 * Get count of girls
 * @param {Object} tripData - Trip plan data
 * @returns {number}
 */
export const getGirlsCount = (tripData) => {
  // This would need to be implemented based on booking data
  // For now, return 0 as girls are not stored in trip plan
  return 0;
};

/**
 * Get check-in progress for a trip
 * @param {Object} tripData - Trip plan data
 * @returns {Object}
 */
export const getCheckinProgress = (tripData) => {
  const checkinConfig = tripData.checkinConfig;
  const checkinStatus = checkinConfig?.status || {
    organizersCheckedIn: [],
    participantsCheckedIn: [],
    girlsCheckedIn: []
  };

  let totalMembers = 0;
  let checkedInMembers = 0;

  // Count organizers
  if (checkinConfig?.enabled?.organizers) {
    const organizersCount = getOrganizersCount(tripData);
    totalMembers += organizersCount;
    checkedInMembers += checkinStatus.organizersCheckedIn?.length || 0;
  }

  // Count participants
  if (checkinConfig?.enabled?.participants) {
    const participantsCount = getParticipantsCount(tripData);
    totalMembers += participantsCount;
    checkedInMembers += checkinStatus.participantsCheckedIn?.length || 0;
  }

  // Count girls
  if (checkinConfig?.enabled?.girls) {
    const girlsCount = getGirlsCount(tripData);
    totalMembers += girlsCount;
    checkedInMembers += checkinStatus.girlsCheckedIn?.length || 0;
  }

  return {
    total: totalMembers,
    checkedIn: checkedInMembers,
    percentage: totalMembers > 0 ? Math.round((checkedInMembers / totalMembers) * 100) : 0,
    organizers: {
      total: checkinConfig?.enabled?.organizers ? getOrganizersCount(tripData) : 0,
      checkedIn: checkinStatus.organizersCheckedIn?.length || 0
    },
    participants: {
      total: checkinConfig?.enabled?.participants ? getParticipantsCount(tripData) : 0,
      checkedIn: checkinStatus.participantsCheckedIn?.length || 0
    },
    girls: {
      total: checkinConfig?.enabled?.girls ? getGirlsCount(tripData) : 0,
      checkedIn: checkinStatus.girlsCheckedIn?.length || 0
    }
  };
};

/**
 * Check if user is checked in for a specific group
 * @param {Object} tripData - Trip plan data
 * @param {string} groupId - Group ID
 * @param {string} userId - User ID
 * @returns {boolean}
 */
export const isUserCheckedIn = (tripData, groupId, userId) => {
  const checkinStatus = tripData.checkinConfig?.status || {
    organizersCheckedIn: [],
    participantsCheckedIn: [],
    girlsCheckedIn: []
  };

  const groupKey = `${groupId}CheckedIn`;
  const checkedInList = checkinStatus[groupKey] || [];
  
  return checkedInList.includes(userId);
};

/**
 * Get check-in statistics for admin dashboard
 * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
 */
export const getCheckinStatistics = async () => {
  try {
    // This would need to be implemented to get statistics across all trips
    // For now, return empty statistics
    return {
      success: true,
      data: {
        totalTrips: 0,
        tripsWithCheckin: 0,
        totalCheckins: 0,
        averageCheckinRate: 0
      }
    };
  } catch (error) {
    console.error('Error getting check-in statistics:', error);
    return {
      success: false,
      error: error.message
    };
  }
};




